import { useEffect } from 'react'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

type CategoryKeys = 'C0002' | 'C0003' | 'C0004' | 'C0005'
type Categories = 'performance' | 'functional' | 'targeting' | 'socialMedia'

type CategoryKeysMap = {
  [key in CategoryKeys]: Categories
}

type CategoriesMap = {
  [key in Categories]: boolean
}

type OneTrustState = {
  consent: CategoriesMap
  isInitialized: boolean
}

type OneTrustActions = {
  updateConsent: (consent: CategoriesMap) => void
  setInitialized: (initialized: boolean) => void
  openSettings: () => void
  close: () => void
  allowAll: () => void
  rejectAll: () => void
  isAlertBoxClosed: () => boolean | undefined
  isAlertBoxClosedAndValid: () => boolean | undefined
}

type OneTrustStore = OneTrustState & OneTrustActions

// Helper function to get active groups from OneTrust
const getActiveGroupsInternal = (): CategoriesMap => {
  const activeGroups =
    typeof OnetrustActiveGroups !== 'undefined'
      ? OnetrustActiveGroups?.split(',')
      : []

  const categories: CategoryKeysMap = {
    C0002: 'performance',
    C0003: 'functional',
    C0004: 'targeting',
    C0005: 'socialMedia',
  }

  return activeGroups.reduce(
    (acc: CategoriesMap, group) => {
      const category = categories[group as CategoryKeys]
      if (category) {
        acc[category] = true
      }
      return acc
    },
    {
      performance: false,
      functional: false,
      targeting: false,
      socialMedia: false,
    } as CategoriesMap,
  )
}

// Create Zustand store with subscribeWithSelector middleware
export const useOneTrustStore = create<OneTrustStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    consent: getActiveGroupsInternal(),
    isInitialized: false,

    // Actions
    updateConsent: (consent: CategoriesMap) => {
      set({ consent, isInitialized: true })
    },

    setInitialized: (isInitialized: boolean) => {
      set({ isInitialized })
    },

    openSettings: () => {
      OneTrust?.ToggleInfoDisplay()
    },

    close: () => {
      OneTrust?.Close()
    },

    allowAll: () => {
      OneTrust?.AllowAll()
    },

    rejectAll: () => {
      OneTrust?.RejectAll()
    },

    isAlertBoxClosed: () => {
      return OneTrust?.IsAlertBoxClosed?.() ?? undefined
    },

    isAlertBoxClosedAndValid: () => {
      return OneTrust?.IsAlertBoxClosedAndValid?.() ?? undefined
    },
  })),
)

// Set up global event listeners and subscribers
let isListenersSetup = false

const setupOneTrustListeners = () => {
  if (isListenersSetup) return
  isListenersSetup = true

  const handleConsentChange = () => {
    const newConsent = getActiveGroupsInternal()
    useOneTrustStore.getState().updateConsent(newConsent)
  }

  // Listen for consent changes
  document.addEventListener('cookie_consent', handleConsentChange)

  // Initialize consent state
  handleConsentChange()
}

// React hook for using OneTrust store
export const useOneTrust = () => {
  const {
    consent,
    isInitialized,
    openSettings,
    close,
    allowAll,
    rejectAll,
    isAlertBoxClosed,
    isAlertBoxClosedAndValid,
  } = useOneTrustStore()

  useEffect(() => {
    setupOneTrustListeners()
  }, [])

  const hasConsent = (category: Categories) => {
    return consent[category]
  }

  const getActiveGroups = () => {
    return consent
  }

  return {
    // State
    consent,
    isInitialized,
    // Consent helpers
    hasConsent,
    getActiveGroups,
    // OneTrust methods
    OpenSettings: openSettings,
    Close: close,
    AllowAll: allowAll,
    RejectAll: rejectAll,
    IsAlertBoxClosed: isAlertBoxClosed,
    IsAlertBoxClosedAndValid: isAlertBoxClosedAndValid,
  }
}
